import React, { useCallback, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import classNames from 'classnames';
import { geoCode } from '../../../../api/mapbox';
import {useTranslation} from '../../../../config/TranslationProvider';
import {useAppConfiguration} from '../../../../AppConfiguration';

const DIRECTION_GEOCODE = 'user-typed';
const DIRECTION_REVERSE_GEOCODE = 'current-position';

const GEOLOCATION_ERRORS = {
    UNKNOWN: 0,
    PERMISSION_DENIED: 1,
    POSITION_UNAVAILABLE: 2,
    TIMEOUT: 3,
};
const geolocationErrorTexts = {
    [GEOLOCATION_ERRORS.UNKNOWN]: 'Wir konnten Ihre Position leider nicht ermitteln.',
    [GEOLOCATION_ERRORS.PERMISSION_DENIED]: 'Die Ermittlung der Position wurde verweigert.',
    [GEOLOCATION_ERRORS.POSITION_UNAVAILABLE]: 'Wir konnten Ihre Position leider nicht ermitteln.',
    [GEOLOCATION_ERRORS.TIMEOUT]: 'Die Ermittlung Ihrer Position wurde abgebrochen, da sie zu lange gedauert hat.',
};

const RadiusCenter = ({center, setCenter}) => {
    const {isWidget} = useAppConfiguration();
    const geoLocation = center.length > 0 ? center.split(',') : false;
    const setGeoLocation = useCallback(coords => {
        if (!coords) {
            setCenter('');

            return;
        }

        setCenter(coords[0] + ',' + coords[1]);
    }, [setCenter]);

    const [direction, setDirection] = useState(geoLocation ? DIRECTION_REVERSE_GEOCODE : DIRECTION_GEOCODE);
    const [loading, setLoading] = useState(false);
    const [displayName, setDisplayName] = useState('');
    const [geolocationErrorCode, setGeolocationErrorCode] = useState(null);

    const setGeolocationErrorCodeTemporary = useCallback(code => {
        setGeolocationErrorCode(code);

        setTimeout(() => setGeolocationErrorCode(null), 5000);
    }, [setGeolocationErrorCode]);

    const messageEventListener = useCallback(({data: {id, position, errorCode}}) => {
        if (!id || (id !== 'dmg-widget-location-success' && id !== 'dmg-widget-location-error')) {
            return;
        }

        const currentPosition = position && JSON.parse(position);
        if (id === 'dmg-widget-location-success' && currentPosition?.latitude) {
            setDirection(DIRECTION_REVERSE_GEOCODE);
            const {latitude, longitude} = currentPosition;
            setGeoLocation([latitude, longitude]);
            setLoading(false);
        } else {
            setLoading(false);
            setGeolocationErrorCodeTemporary(errorCode || GEOLOCATION_ERRORS.UNKNOWN);
        }
    }, [setGeoLocation, setGeolocationErrorCodeTemporary]);

    useEffect(() => {
        if (isWidget) {
            window.addEventListener('message', messageEventListener);
        }

        return () => window.removeEventListener('message', messageEventListener);
    }, [isWidget, messageEventListener]);

    function getCurrentGeoLocation() {
        setDirection(DIRECTION_REVERSE_GEOCODE);
        setGeoLocation(false); // Don't execute query until we got the current position from the browser
        setLoading(true);

        if (isWidget) {
            window.parent.postMessage(
                {id: 'dmg-widget-location'},
                '*'
            );
        } else {
            navigator.geolocation.getCurrentPosition(
                position => {
                    const {latitude, longitude} = position.coords;

                    setGeoLocation([latitude, longitude]);
                    setLoading(false);
                },
                error => {
                    setLoading(false);

                    setGeolocationErrorCodeTemporary(error.code || GEOLOCATION_ERRORS.UNKNOWN);
                },
                {timeout: 7000}
            );
        }
    }

    // Resolve current user position to a readable name
    const {data: resolvedUserPosition, isLoading: resolvedUserPositionLoading} = useQuery(
        ['geo-location-reverse-geocode', geoLocation],
        async () => {
            const data = await geoCode(geoLocation.reverse().join(','), {limit: 1});

            return data.features[0]?.place_name_de.replace(', Deutschland', '');
        },
        {
            enabled: direction === DIRECTION_REVERSE_GEOCODE && geoLocation,
        }
    );

    // Update display name based on the resolved user position
    if (direction === DIRECTION_REVERSE_GEOCODE && resolvedUserPosition && resolvedUserPosition !== displayName) {
        setDisplayName(resolvedUserPosition);
    }

    // Geo code user typed location
    useEffect(() => {
        if (direction === DIRECTION_REVERSE_GEOCODE) {
            // Don't execute if we reverse geocode
            return;
        }

        if (displayName === '') {
            // Ignore empty display names
            return;
        }

        const debounceTimeout = setTimeout(async () => {
            setLoading(true);

            const data = await geoCode(displayName, {
                // Limit search results to Dresden and the surrounding area (Same as the backend bounding box).
                // Can be removed once we show suggestions to the user instead of just using the first matching result.
                bbox: '13.283893,50.693548,14.361097,51.360378'
            });

            // Use first result by default
            let location = data.features[0];

            // If any of the results exactly match the display name, use this one instead
            // We need this because if the user e.g. searches for "Pirna", we want to return the city Pirna and not the
            // street "Pirnaische Straße" in Dresden.
            for (const feature of data.features) {
                if (feature.text_de?.toLowerCase() === displayName?.toLowerCase()) {
                    location = feature;

                    break;
                }
            }

            setGeoLocation(location && [location.center[1], location.center[0]]);
            setLoading(false);
        }, 500);

        return () => clearTimeout(debounceTimeout);
    }, [direction, displayName, setGeoLocation]);

    return <>
        {'geolocation' in navigator && (
            <div
                className={classNames({
                    'use-current-location': true,
                    'la la-crosshairs': !loading && !resolvedUserPositionLoading,
                    'loading': loading || resolvedUserPositionLoading,
                    'active': direction === DIRECTION_REVERSE_GEOCODE,
                })}
                onClick={getCurrentGeoLocation}
            >
                {(loading || resolvedUserPositionLoading) && (
                    <div className="spinner-border spinner-border-sm" />
                )}
            </div>
        )}
        <input
            type="text"
            placeholder={useTranslation('location_search')}
            className="form-control"
            value={displayName}
            onChange={e => {
                // User manually changed the location, change direction and reset geo location (will be re-retrieved
                // when the user stops typing)
                setDirection(DIRECTION_GEOCODE);
                setGeoLocation(false);

                // Update display name
                setDisplayName(e.target.value);
            }}
            aria-label="text"
            autoComplete="postal-code"
        />
        {geolocationErrorCode && (
            <span style={{color: 'red', fontSize: '0.8rem'}} >
                <strong>Fehler:</strong> {geolocationErrorTexts[geolocationErrorCode]}
            </span>
        )}
    </>;
};

export default RadiusCenter;
